import React, { useState, useEffect, useMemo } from 'react';
import { router, Head, usePage } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Chrome,
  Archive,
  RefreshCw,
  MinusCircle,
  Plug,
  BarChart3,
  Trash2,
  Search,
  Check,
  Megaphone,
  Plus,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import AssignWorkersModal from '@/frontend/components/Integrations/AssignWorkersModal';
import { ConnectionStatusChip } from '@/frontend/components/Integrations/ConnectionStatusChip';
import AssignedWorkersBanner from '@/frontend/components/Integrations/AssignedWorkersBanner';
import PostConnectionModal from '@/frontend/components/PostConnectionModal';
import { usePermissions } from '@/frontend/hooks/usePermissions';
import { useJustConnectedWorker } from '@/frontend/hooks/useJustConnectedWorker';
import DashboardLayout from '@/frontend/layouts/DashboardLayout';
import { BackLink } from '@/frontend/components/BackLink';
import type {
  GoogleAnalyticsAccount,
  AvailableGoogleAnalyticsAccount,
  GoogleAdsAccount,
  AvailableGoogleAdsAccount,
  AssignWorker,
} from './types';

interface PageProps {
  available_google_analytics_accounts: AvailableGoogleAnalyticsAccount[];
  [key: string]: any;
}

// Cabeçalho de tabela canônico do portal (design.md: th 11/650 uppercase baixa).
const TH_CLS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-faint-foreground';

interface Props {
  page_title: string;
  google_connected: boolean;
  google_analytics_imported: boolean;
  google_ads_imported: boolean;
  available_google_analytics_accounts: AvailableGoogleAnalyticsAccount[];
  available_google_ads_accounts: AvailableGoogleAdsAccount[];
  user_google_analytics_accounts: GoogleAnalyticsAccount[];
  user_google_ads_accounts: GoogleAdsAccount[];
  digital_workers: AssignWorker[];
}

const GooglePage = ({
  page_title,
  google_connected,
  google_analytics_imported,
  google_ads_imported,
  available_google_analytics_accounts = [],
  available_google_ads_accounts = [],
  user_google_analytics_accounts = [],
  user_google_ads_accounts = [],
  digital_workers = [],
}: Props) => {
  const { can_manage_resources } = usePermissions();
  // Google Analytics state
  const [gaDialogOpen, setGaDialogOpen] = useState(false);
  const [gaIsLoading, setGaIsLoading] = useState(false);
  const [gaAccounts, setGaAccounts] = useState<AvailableGoogleAnalyticsAccount[]>(available_google_analytics_accounts);
  const [userGaAccounts, setUserGaAccounts] = useState<GoogleAnalyticsAccount[]>(user_google_analytics_accounts);
  const [gaSearchTerm, setGaSearchTerm] = useState('');
  const [gaLoadingById, setGaLoadingById] = useState<Record<string, boolean>>({});
  const [gaConnectedSearchTerm, setGaConnectedSearchTerm] = useState('');
  const [propertyLoadingById, setPropertyLoadingById] = useState<Record<string, boolean>>({});

  // Google Ads state
  const [googleAdsDialogOpen, setGoogleAdsDialogOpen] = useState(false);
  const [googleAdsIsLoading, setGoogleAdsIsLoading] = useState(false);
  const [googleAdsAccounts, setGoogleAdsAccounts] = useState<AvailableGoogleAdsAccount[]>(available_google_ads_accounts);
  const [userGoogleAdsAccounts, setUserGoogleAdsAccounts] = useState<GoogleAdsAccount[]>(user_google_ads_accounts);
  const [googleAdsSearchTerm, setGoogleAdsSearchTerm] = useState('');
  const [googleAdsConnectedSearchTerm, setGoogleAdsConnectedSearchTerm] = useState('');
  const { flash } = usePage<{ flash: { assign_workers?: boolean } }>().props;
  const [assignWorkersOpen, setAssignWorkersOpen] = useState(
    () => !!flash?.assign_workers && digital_workers.length > 0
  );
  const [savingWorkerAssignment, setSavingWorkerAssignment] = useState(false);
  const justConnected = useJustConnectedWorker(digital_workers);

  useEffect(() => {
    if (user_google_analytics_accounts) setUserGaAccounts(user_google_analytics_accounts);
  }, [user_google_analytics_accounts]);

  useEffect(() => {
    if (available_google_analytics_accounts && available_google_analytics_accounts.length > 0) {
      setGaAccounts(available_google_analytics_accounts);
    }
  }, [available_google_analytics_accounts]);

  useEffect(() => {
    if (user_google_ads_accounts) setUserGoogleAdsAccounts(user_google_ads_accounts);
  }, [user_google_ads_accounts]);

  useEffect(() => {
    if (available_google_ads_accounts && available_google_ads_accounts.length > 0) {
      setGoogleAdsAccounts(available_google_ads_accounts);
    }
  }, [available_google_ads_accounts]);

  const filteredGaAccounts = useMemo(() => {
    if (!gaConnectedSearchTerm.trim()) return userGaAccounts;
    const term = gaConnectedSearchTerm.toLowerCase();
    return userGaAccounts.filter(account =>
      account.name.toLowerCase().includes(term) ||
      account.external_id.toLowerCase().includes(term) ||
      account.google_analytics_properties?.some(property =>
        property.name.toLowerCase().includes(term)
      )
    );
  }, [userGaAccounts, gaConnectedSearchTerm]);

  const filteredGoogleAdsConnectedAccounts = useMemo(() => {
    if (!googleAdsConnectedSearchTerm.trim()) return userGoogleAdsAccounts;
    const term = googleAdsConnectedSearchTerm.toLowerCase();
    return userGoogleAdsAccounts.filter(account =>
      account.name.toLowerCase().includes(term) ||
      account.external_id.toLowerCase().includes(term)
    );
  }, [userGoogleAdsAccounts, googleAdsConnectedSearchTerm]);

  const fetchAvailableGoogleAnalyticsAccounts = async () => {
    setGaIsLoading(true);
    router.reload({
      only: ['available_google_analytics_accounts'],
      onSuccess: (page) => {
        const data = page.props as unknown as PageProps;
        if (data.available_google_analytics_accounts) {
          const existingAccountIds = userGaAccounts.map(acc => acc.external_id);
          const availableAccounts = data.available_google_analytics_accounts.map((account: AvailableGoogleAnalyticsAccount) => ({
            ...account,
            imported: existingAccountIds.includes(account.external_id)
          }));
          setGaAccounts(availableAccounts);
        } else {
          setGaAccounts([]);
        }
      },
      onError: () => setGaAccounts([]),
      onFinish: () => setGaIsLoading(false)
    });
  };

  const toggleGaAccountSelection = (accountId: string) => {
    setGaAccounts(accounts =>
      accounts.map(account =>
        account.external_id === accountId ? { ...account, selected: !account.selected } : account
      )
    );
  };

  const importSelectedGaAccounts = async () => {
    const selectedAccounts = gaAccounts.filter(account => account.selected && !account.imported);
    if (selectedAccounts.length === 0) return;
    setGaIsLoading(true);
    try {
      router.post('/dashboard/integrations/google_analytics_accounts', {
        accounts: selectedAccounts.map(acc => ({
          external_id: acc.external_id,
          name: acc.name,
          properties: acc.properties || [],
        }))
      }, {
        onError: () => console.error('Error importing GA accounts'),
        onFinish: () => setGaIsLoading(false)
      });
    } catch (error) {
      console.error('Error importing GA accounts:', error);
      setGaIsLoading(false);
    }
  };

  const togglePropertySelection = async (accountId: string, propertyId: string, currentlySelected: boolean) => {
    const loadingKey = `${accountId}-${propertyId}`;
    setPropertyLoadingById(prev => ({ ...prev, [loadingKey]: true }));
    try {
      const url = currentlySelected
        ? `/dashboard/integrations/google_analytics_accounts/${accountId}/properties/${propertyId}/disconnect`
        : `/dashboard/integrations/google_analytics_accounts/${accountId}/properties/${propertyId}/connect`;
      const response = await fetch(url, {
        method: currentlySelected ? 'DELETE' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
      });
      if (response.ok) {
        setUserGaAccounts(prev =>
          prev.map(account =>
            account.id === accountId
              ? {
                  ...account,
                  google_analytics_properties: account.google_analytics_properties.map(prop =>
                    prop.id === propertyId ? { ...prop, selected: !currentlySelected } : prop
                  )
                }
              : account
          )
        );
      }
    } catch (error) {
      console.error('Error toggling property selection:', error);
    } finally {
      setPropertyLoadingById(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const removeGoogleAnalyticsAccount = async (accountId: string) => {
    if (!confirm('Tem certeza que deseja remover esta conta do Google Analytics?')) return;
    setGaLoadingById(prev => ({ ...prev, [`remove-${accountId}`]: true }));
    router.delete(`/dashboard/integrations/google_analytics_accounts/${accountId}`, {
      preserveScroll: true,
      onError: () => setGaLoadingById(prev => ({ ...prev, [`remove-${accountId}`]: false })),
      onFinish: () => setGaLoadingById(prev => ({ ...prev, [`remove-${accountId}`]: false }))
    });
  };

  const fetchAvailableGoogleAdsAccounts = async () => {
    if (!can_manage_resources) return;
    setGoogleAdsIsLoading(true);
    try {
      const response = await fetch('/dashboard/integrations/google_ads_accounts/available', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });
      const data = await response.json();
      if (data.success) {
        setGoogleAdsAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Failed to fetch Google Ads accounts:', error);
    } finally {
      setGoogleAdsIsLoading(false);
    }
  };

  const removeGoogleAdsAccount = async (accountId: string) => {
    if (!can_manage_resources) return;
    router.delete(`/dashboard/integrations/google_ads_accounts/${accountId}`, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => setUserGoogleAdsAccounts(prev => prev.filter(account => account.id !== accountId)),
      onError: (errors: any) => console.error('Error removing Google Ads account:', errors)
    });
  };

  return (
    <>
      <Head title={page_title} />
      <div className="px-4 py-6">
        <div className="space-y-6 min-h-full">
          {/* Cabeçalho único (padrão da ficha de credencial): breadcrumb fantasma +
              quadrado do ícone + nome + chip de status + ações. O "brilho"
              gradiente, o checkmark e o título central duplicado saíram. */}
          <BackLink label="Integrações" fallbackHref="/dashboard/integrations" />

          <div className="flex items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-secondary [&_svg]:size-6">
              <Chrome className="text-muted-foreground" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-foreground">Google</h1>
                <ConnectionStatusChip connected={google_connected} />
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1 sm:gap-2">
              {can_manage_resources ? (
                <a href="/dashboard/integrations/connect_google">
                  <Button variant="outline" size="sm">
                    {google_connected ? <RefreshCw className="w-3 sm:w-4" /> : <Plug className="w-3 sm:w-4" />}
                    {google_connected ? (
                      <>
                        <span className="hidden sm:inline">Reconectar</span>
                        <span className="sm:hidden">Recon.</span>
                      </>
                    ) : (
                      <>
                        <span className="hidden sm:inline">Conectar</span>
                        <span className="sm:hidden">Conectar</span>
                      </>
                    )}
                  </Button>
                </a>
              ) : (
                <Button variant="outline" size="sm" disabled>
                  {google_connected ? <RefreshCw className="w-3 sm:w-4" /> : <Plug className="w-3 sm:w-4" />}
                  {google_connected ? (
                    <>
                      <span className="hidden sm:inline">Reconectar</span>
                      <span className="sm:hidden">Recon.</span>
                    </>
                  ) : (
                    <>
                      <span className="hidden sm:inline">Conectar</span>
                      <span className="sm:hidden">Conectar</span>
                    </>
                  )}
                </Button>
              )}
              {google_connected && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!can_manage_resources}
                  className="text-muted-foreground hover:border-destructive/40 hover:text-destructive"
                  onClick={() => {
                    if (!can_manage_resources) return;
                    if (confirm('Tem certeza que deseja desconectar o Google?')) {
                      router.delete('/dashboard/integrations/disconnect_google', { preserveScroll: true });
                    }
                  }}
                >
                  <span className="hidden sm:inline">Desconectar</span>
                  <span className="sm:hidden">Desc.</span>
                </Button>
              )}
            </div>
          </div>

          {!can_manage_resources && (
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Você não tem permissão para gerenciar integrações.</AlertTitle>
              <AlertDescription>Você pode visualizar as integrações conectadas, mas não pode modificá-las.</AlertDescription>
            </Alert>
          )}

          <Card><CardContent className="p-6">
            <div className="space-y-4">
              {!google_connected && !google_analytics_imported && (
                <div className="space-y-4">
                  <p className="text-[13px] text-muted-foreground">
                    Conecte sua conta do Google para importar dados do Google Analytics e Google Ads.
                  </p>
                </div>
              )}

              {google_connected && !google_analytics_imported && (
                <div className="space-y-4">
                  <p className="flex items-center gap-2 text-[13px] text-success">
                    <Check className="h-4 w-4" />
                    Google conectado — agora você pode importar contas do Analytics e Google Ads.
                  </p>
                </div>
              )}

              {!google_connected && google_analytics_imported && (
                <div className="space-y-4">
                  <p className="flex items-center gap-2 text-[13px] text-warning">
                    <MinusCircle className="h-4 w-4" />
                    Token expirado — reconecte o Google para gerenciar contas do Analytics e Google Ads.
                  </p>
                </div>
              )}

              {/* Google Analytics Section */}
              {(google_analytics_imported || google_connected) && (
                <div className="space-y-6 mt-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-[15px] font-semibold text-foreground mb-1">Google Analytics</h3>
                      <p className="text-muted-foreground text-[13px] hidden sm:block">Gerencie suas contas do Google Analytics conectadas</p>
                    </div>
                    <Button
                      onClick={() => {
                        if (!can_manage_resources || !google_connected) return;
                        setGaDialogOpen(true);
                        fetchAvailableGoogleAnalyticsAccounts();
                      }}
                      variant="outline"
                      size="sm"
                      disabled={!can_manage_resources || !google_connected}
                      className="w-full sm:w-auto"
                    >
                      <Plug className="w-4" />
                      Conectar contas
                    </Button>
                  </div>

                  {userGaAccounts.length > 0 && (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input type="search" className="pl-9" placeholder="Buscar contas..." value={gaConnectedSearchTerm} onChange={(e) => setGaConnectedSearchTerm(e.target.value)} />
                    </div>
                  )}

                  {userGaAccounts.length > 0 ? (
                    <div className="space-y-3">
                      {gaConnectedSearchTerm && (
                        <div className="text-[13px] text-muted-foreground mb-2">
                          {filteredGaAccounts.length} resultado(s) de {userGaAccounts.length} conta(s)
                        </div>
                      )}
                      {filteredGaAccounts.length > 0 ? (
                        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                          {filteredGaAccounts.map((account) => {
                            const selectedPropertiesCount = account.google_analytics_properties?.filter(prop => prop.selected).length || 0;
                            const totalPropertiesCount = account.google_analytics_properties?.length || 0;

                            return (
                              <div key={account.id} className="relative bg-background/30 border border-border/50 rounded-lg hover:bg-background/50 hover:border-border/50 transition-all duration-200">
                                <div className="p-4">
                                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                    <div className="flex items-start justify-between md:hidden">
                                      <div className="flex items-center gap-3 min-w-0 flex-1 mr-3">
                                        <div className="p-2 bg-secondary rounded-lg shrink-0">
                                          <BarChart3 className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <div className="min-w-0">
                                          <div className="font-medium text-foreground text-[13px] truncate" title={account.name}>{account.name}</div>
                                          <div className="text-[13px] text-muted-foreground">
                                            {selectedPropertiesCount} de {totalPropertiesCount} {totalPropertiesCount === 1 ? 'propriedade conectada' : 'propriedades conectadas'}
                                          </div>
                                        </div>
                                      </div>
                                      <Badge variant="success" className="text-[11px] px-1.5">Conectada</Badge>
                                    </div>
                                    <div className="hidden md:flex md:items-center md:flex-1 md:gap-6">
                                      <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <div className="p-2 bg-secondary rounded-lg shrink-0">
                                          <BarChart3 className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <div className="min-w-0">
                                          <div className="font-medium text-foreground text-[13px] truncate" title={account.name}>{account.name}</div>
                                          <div className="text-[13px] text-muted-foreground">
                                            {selectedPropertiesCount} de {totalPropertiesCount} {totalPropertiesCount === 1 ? 'propriedade conectada' : 'propriedades conectadas'}
                                          </div>
                                        </div>
                                      </div>
                                      <Badge variant="success" className="text-[11px] px-1.5">Conectada</Badge>
                                    </div>
                                    <div className="flex items-center justify-end gap-2 mt-3 md:mt-0">
                                      <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        onClick={() => can_manage_resources && removeGoogleAnalyticsAccount(account.id)}
                                        disabled={gaLoadingById[`remove-${account.id}`] || !can_manage_resources}
                                        className={!can_manage_resources ? 'opacity-50' : ''}
                                      >
                                        {gaLoadingById[`remove-${account.id}`] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                      </Button>
                                    </div>
                                  </div>
                                </div>

                                {totalPropertiesCount > 0 && (
                                  <div className="border-t border-border/50 p-4 bg-muted/50">
                                    <div className="mb-4">
                                      <h4 className="text-[13px] font-medium text-foreground">Selecionar propriedades do Google Analytics</h4>
                                    </div>
                                    <div className="grid gap-2 max-h-60 overflow-y-auto">
                                      {account.google_analytics_properties?.map((property) => {
                                        const loadingKey = `${account.id}-${property.id}`;
                                        const isPropertyLoading = propertyLoadingById[loadingKey];
                                        return (
                                          <div key={property.id} className="flex items-center justify-between p-3 bg-card rounded-lg border border-border hover:border-border transition-colors">
                                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                              <Checkbox
                                                checked={property.selected || false}
                                                onCheckedChange={() => can_manage_resources && google_connected && togglePropertySelection(account.id, property.id, property.selected || false)}
                                                disabled={isPropertyLoading || !can_manage_resources || !google_connected}
                                              />
                                              <div className="min-w-0 flex-1">
                                                <div className="font-medium text-foreground text-[13px] truncate" title={property.name}>{property.name}</div>
                                                <div className="text-[11.5px] text-muted-foreground">
                                                  ID: {property.external_id}
                                                  {property.organization_property && (
                                                    <span className="ml-2 text-brand">• Organização: {property.organization_name}</span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                            {isPropertyLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Search className="h-8 w-8 mx-auto mb-2 opacity-50 text-muted-foreground" />
                          <h4 className="text-[15px] font-semibold text-foreground mb-2">Nenhuma conta encontrada</h4>
                          <p className="text-muted-foreground text-[13px]">Tente alterar os termos da sua busca.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="mb-4 inline-flex rounded-full bg-brand/10 p-4">
                        <BarChart3 className="h-8 w-8 text-brand" />
                      </div>
                      <h4 className="text-[15px] font-semibold text-foreground mb-2">Nenhuma conta conectada</h4>
                      <p className="text-muted-foreground mb-4 text-[13px] max-w-sm mx-auto">Conecte suas contas do Google Analytics para visualizar métricas e relatórios.</p>
                      <Button
                        onClick={() => {
                          if (!can_manage_resources) return;
                          setGaDialogOpen(true);
                          fetchAvailableGoogleAnalyticsAccounts();
                        }}
                        size="sm"
                        disabled={!can_manage_resources}
                      >
                        <Plug className="w-4" />
                        Conectar primeira conta
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Google Ads Section */}
              {(google_ads_imported || google_connected) && (
                <div className="space-y-6 mt-4 border-t border-border pt-6">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-[15px] font-semibold text-foreground mb-1">Google Ads</h3>
                      <p className="text-muted-foreground text-[13px] hidden sm:block">Gerencie suas contas do Google Ads conectadas</p>
                    </div>
                    <Button
                      onClick={() => {
                        if (!can_manage_resources || !google_connected) return;
                        setGoogleAdsDialogOpen(true);
                        fetchAvailableGoogleAdsAccounts();
                      }}
                      variant="outline"
                      size="sm"
                      disabled={!can_manage_resources || !google_connected}
                      className="w-full sm:w-auto"
                    >
                      <Plus className="w-4" />
                      Conectar contas do Google Ads
                    </Button>
                  </div>

                  {userGoogleAdsAccounts && userGoogleAdsAccounts.length > 0 ? (
                    <div className="space-y-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input type="search" className="pl-9" placeholder="Buscar contas do Google Ads..." value={googleAdsConnectedSearchTerm} onChange={(e) => setGoogleAdsConnectedSearchTerm(e.target.value)} />
                      </div>
                      {googleAdsConnectedSearchTerm && (
                        <div className="text-[13px] text-muted-foreground mb-2">
                          {filteredGoogleAdsConnectedAccounts.length} resultado(s) de {userGoogleAdsAccounts.length} conta(s)
                        </div>
                      )}
                      {filteredGoogleAdsConnectedAccounts.length > 0 ? (
                        <div className="relative">
                          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                            {filteredGoogleAdsConnectedAccounts.map((account) => {
                              const canManage = can_manage_resources;
                              return (
                                <div key={account.id} className="group">
                                  <div className="relative bg-background/30 border border-border/50 rounded-lg p-4 hover:bg-background/50 hover:border-border/50 transition-all duration-200">
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                      <div className="flex items-start justify-between md:hidden">
                                        <div className="min-w-0 flex-1 mr-3">
                                          <h3 className="font-medium text-foreground text-[13px] truncate" title={account.name}>{account.name}</h3>
                                          <p className="text-muted-foreground font-mono text-[11.5px] mt-0.5">{account.external_id}</p>
                                        </div>
                                        <Badge variant="success" className="text-[11px] px-1.5">Conectada</Badge>
                                      </div>
                                      <div className="hidden md:flex md:items-center md:flex-1 md:gap-6">
                                        <div className="min-w-0 flex-1">
                                          <h3 className="font-medium text-foreground text-[13px] truncate" title={account.name}>{account.name}</h3>
                                          <p className="text-muted-foreground font-mono text-[11.5px] mt-0.5">{account.external_id}</p>
                                        </div>
                                        <Badge variant="success" className="text-[11px] px-1.5">Conectada</Badge>
                                      </div>
                                      <div className="flex items-center justify-end gap-2 mt-3 md:mt-0">
                                        <Button
                                          variant="ghost"
                                          size="icon-sm"
                                          onClick={() => canManage && removeGoogleAdsAccount(account.id)}
                                          disabled={!canManage}
                                          className={!canManage ? 'opacity-50' : ''}
                                        >
                                          <MinusCircle className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Search className="h-8 w-8 mx-auto mb-2 opacity-50 text-muted-foreground" />
                          <h4 className="text-[15px] font-semibold text-foreground mb-2">Nenhuma conta encontrada</h4>
                          <p className="text-muted-foreground text-[13px]">Tente alterar os termos da sua busca.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="mb-4 inline-flex rounded-full bg-secondary p-4">
                        <Megaphone className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <h4 className="text-[15px] font-semibold text-foreground mb-2">Nenhuma conta conectada</h4>
                      <p className="text-muted-foreground mb-4 text-[13px] max-w-sm mx-auto">Conecte suas contas do Google Ads para gerenciar campanhas e anúncios.</p>
                      <Button
                        onClick={() => {
                          if (!can_manage_resources) return;
                          setGoogleAdsDialogOpen(true);
                          fetchAvailableGoogleAdsAccounts();
                        }}
                        size="sm"
                        disabled={!can_manage_resources}
                      >
                        <Plug className="w-4" />
                        Conectar primeira conta
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent></Card>

          <AssignedWorkersBanner
            workers={digital_workers.filter((w) => w.assigned)}
            onAddWorker={() => setAssignWorkersOpen(true)}
            onRemoveWorker={(workerId) => router.delete('/dashboard/integrations/remove_worker', { data: { provider: 'google', worker_id: workerId }, preserveScroll: true })}
          />
        </div>
      </div>

      {/* Google Analytics Accounts Dialog */}
      <Dialog open={gaDialogOpen} onOpenChange={setGaDialogOpen}>
        <DialogContent className="max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Contas do Google Analytics disponíveis</DialogTitle>
            <DialogDescription>Selecione as contas do Google Analytics que deseja conectar ao sistema</DialogDescription>
          </DialogHeader>

          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="search" className="pl-9" placeholder="Buscar por nome..." value={gaSearchTerm} onChange={(e) => setGaSearchTerm(e.target.value)} />
            </div>
          </div>

          {(() => {
            const filteredAccounts = gaAccounts.filter(account =>
              account.name.toLowerCase().includes(gaSearchTerm.toLowerCase()) ||
              account.external_id.toLowerCase().includes(gaSearchTerm.toLowerCase())
            );

            if (gaIsLoading) {
              return (
                <div className="flex justify-center items-center py-12">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span>Carregando contas...</span>
                  </div>
                </div>
              );
            } else if (filteredAccounts.length > 0) {
              return (
                <div className="border border-border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-border bg-muted">
                        <TableHead className="w-12">{''}</TableHead>
                        <TableHead className={TH_CLS}>Conta</TableHead>
                        <TableHead className={TH_CLS}>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAccounts.map((account) => (
                        <TableRow
                          key={account.external_id}
                          onClick={() => toggleGaAccountSelection(account.external_id)}
                          className={`border-b border-border cursor-pointer transition-all duration-200 ease-in-out ${
                            account.selected ? 'bg-brand/10 hover:bg-brand/15' : 'hover:bg-accent/30'
                          }`}
                        >
                          <TableCell className="w-12">
                            <Checkbox checked={account.selected || false} onCheckedChange={() => toggleGaAccountSelection(account.external_id)} />
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <div className="font-medium text-foreground text-[13px]">{account.name}</div>
                              <div className="text-[13px] text-muted-foreground">ID: {account.external_id}</div>
                              {account.properties && account.properties.length > 0 && (
                                <div className="text-[11.5px] text-muted-foreground">
                                  {account.properties.length} {account.properties.length === 1 ? 'propriedade' : 'propriedades'}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={account.imported ? 'success' : 'info'}>
                              {account.imported ? 'Conectada' : 'Disponível'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              );
            } else if (gaAccounts.length === 0) {
              return (
                <div className="text-center py-8">
                  <div className="mb-4 inline-flex rounded-full bg-destructive/10 p-3">
                    <MinusCircle className="h-12 w-12 text-destructive" />
                  </div>
                  <p className="text-[15px] font-medium text-foreground mb-2">Nenhuma conta encontrada</p>
                  <p className="text-muted-foreground">Não foram encontradas contas do Google Analytics em sua conta Google.</p>
                </div>
              );
            } else {
              return (
                <div className="text-center py-8">
                  <div className="mb-4 inline-flex rounded-full bg-warning/10 p-3">
                    <Archive className="h-12 w-12 text-warning" />
                  </div>
                  <p className="text-[15px] font-medium text-foreground mb-2">Nenhuma conta encontrada</p>
                  <p className="text-muted-foreground">Sua busca não retornou resultados. Tente com outros termos.</p>
                </div>
              );
            }
          })()}

          <DialogFooter>
            {(() => {
              const hasImportable = gaAccounts.some(account => !account.imported) && !gaIsLoading && gaAccounts.filter(account =>
                account.name.toLowerCase().includes(gaSearchTerm.toLowerCase()) ||
                account.external_id.toLowerCase().includes(gaSearchTerm.toLowerCase())
              ).length > 0;

              if (hasImportable) {
                return (
                  <>
                    <Button variant="outline" onClick={() => setGaDialogOpen(false)}>Cancelar</Button>
                    <Button
                      disabled={gaIsLoading || !gaAccounts.some(account => account.selected && !account.imported)}
                      onClick={() => { importSelectedGaAccounts(); setGaDialogOpen(false); }}
                    >
                      {gaIsLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Conectando...</> : 'Conectar selecionadas'}
                    </Button>
                  </>
                );
              }
              return <Button variant="outline" onClick={() => setGaDialogOpen(false)}>Fechar</Button>;
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AssignWorkersModal
        open={assignWorkersOpen}
        onClose={() => setAssignWorkersOpen(false)}
        providerLabel="Google"
        workers={digital_workers}
        provider="google"
        saving={savingWorkerAssignment}
        onSavingChange={setSavingWorkerAssignment}
      />

      {justConnected.worker && (
        <PostConnectionModal
          open={justConnected.open}
          onClose={justConnected.close}
          worker={justConnected.worker}
          context="Google"
        />
      )}

      {/* Google Ads Accounts Dialog */}
      <Dialog open={googleAdsDialogOpen} onOpenChange={setGoogleAdsDialogOpen}>
        <DialogContent className="max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Contas do Google Ads disponíveis</DialogTitle>
            <DialogDescription>Selecione as contas do Google Ads que deseja conectar ao sistema</DialogDescription>
          </DialogHeader>

          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="search" className="pl-9" placeholder="Buscar por nome ou ID..." value={googleAdsSearchTerm} onChange={(e) => setGoogleAdsSearchTerm(e.target.value)} />
            </div>
          </div>

          {(() => {
            const term = googleAdsSearchTerm.toLowerCase();
            const filteredGoogleAds = term
              ? googleAdsAccounts.filter(account =>
                  account.name.toLowerCase().includes(term) || account.external_id.toLowerCase().includes(term)
                )
              : googleAdsAccounts;

            if (googleAdsIsLoading) {
              return (
                <div className="flex justify-center items-center py-12">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span>Carregando contas...</span>
                  </div>
                </div>
              );
            } else if (filteredGoogleAds.length > 0) {
              return (
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {filteredGoogleAds.map((account) => (
                    <div
                      key={account.external_id}
                      className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                        account.imported
                          ? 'border-border bg-muted'
                          : account.selected
                          ? 'border-brand bg-brand/10'
                          : 'border-border bg-background hover:border-brand hover:bg-brand/10'
                      }`}
                      onClick={() => {
                        if (!account.imported) {
                          setGoogleAdsAccounts(prev =>
                            prev.map(acc => acc.external_id === account.external_id ? { ...acc, selected: !acc.selected } : acc)
                          );
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="rounded-lg bg-brand/10 p-2">
                            <Megaphone className="h-4 w-4 text-brand" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground text-[13px]">{account.name}</p>
                            <p className="text-[11.5px] text-muted-foreground">ID: {account.external_id}</p>
                            {account.currency_code && <p className="text-[11.5px] text-muted-foreground">Moeda: {account.currency_code}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {account.imported ? (
                            <Badge variant="info">Já conectada</Badge>
                          ) : (
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              account.selected ? 'bg-brand border-brand' : 'border-border'
                            }`}>
                              {account.selected && <Check className="w-3 h-3 text-brand-foreground" />}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            } else if (googleAdsAccounts.length === 0) {
              return (
                <div className="text-center py-8">
                  <div className="mb-4 inline-flex rounded-full bg-secondary p-3">
                    <Megaphone className="h-12 w-12 text-muted-foreground" />
                  </div>
                  <p className="text-[15px] font-medium text-foreground mb-2">Nenhuma conta do Google Ads encontrada</p>
                  <p className="text-muted-foreground">Não foram encontradas contas do Google Ads em sua conta Google.</p>
                </div>
              );
            } else {
              return (
                <div className="text-center py-8">
                  <div className="mb-4 inline-flex rounded-full bg-warning/10 p-3">
                    <Archive className="h-12 w-12 text-warning" />
                  </div>
                  <p className="text-[15px] font-medium text-foreground mb-2">Nenhuma conta encontrada</p>
                  <p className="text-muted-foreground">Sua busca não retornou resultados. Tente com outros termos.</p>
                </div>
              );
            }
          })()}

          <DialogFooter>
            {(() => {
              const hasImportable = googleAdsAccounts.some(account => !account.imported) && !googleAdsIsLoading && googleAdsAccounts.filter(account =>
                account.name.toLowerCase().includes(googleAdsSearchTerm.toLowerCase()) ||
                account.external_id.toLowerCase().includes(googleAdsSearchTerm.toLowerCase())
              ).length > 0;

              if (hasImportable) {
                return (
                  <>
                    <Button variant="outline" onClick={() => setGoogleAdsDialogOpen(false)}>Cancelar</Button>
                    <Button
                      disabled={googleAdsIsLoading || !googleAdsAccounts.some(acc => acc.selected && !acc.imported)}
                      onClick={() => {
                        const selectedAccounts = googleAdsAccounts.filter(acc => acc.selected && !acc.imported);
                        if (selectedAccounts.length > 0) {
                          router.post('/dashboard/integrations/google_ads_accounts', {
                            accounts: selectedAccounts.map(acc => ({ external_id: acc.external_id, name: acc.name }))
                          }, {
                            preserveState: true,
                            preserveScroll: true,
                            onSuccess: () => setGoogleAdsDialogOpen(false)
                          });
                        }
                      }}
                    >
                      {googleAdsIsLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Conectando...</> : `Conectar selecionadas (${googleAdsAccounts.filter(acc => acc.selected && !acc.imported).length})`}
                    </Button>
                  </>
                );
              }
              return <Button variant="outline" onClick={() => setGoogleAdsDialogOpen(false)}>Fechar</Button>;
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

GooglePage.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>;

export default GooglePage;
